import { useEffect, useMemo, useState } from 'react'
import useConfig from './useConfig'
import { useTranslation } from 'react-i18next'
import useBalances from './useBalances'
import { CKBTC_LEDGER } from '../constants/canisterIds'
import { approveToken } from '../handlers/walletActions/wallet'
import useWallet from './useWallet'
import { keychainManager } from '../keyring'
import getAgent from '../utils/getAgent'
import { useUserTokens } from '../state'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { GAEventName } from '../interfaces/googleAnalytics'
import { ODIN_BACKEND_CANISTER } from '../constants/canisterIds'
import OdinClient from '../clients/OdinClient'
import { CKBTC_ICP_ID, ICP_ICP_ID, ODINBTC_ID } from '../constants/tokenIds'
import { PlugToken } from '../interfaces/common'
import { formatNumber } from '../utils/formatNumber'
import useSwap from './useSwap'
import { useSwapContext } from '../providers/SwapProvider'
import { getTokenBalance } from '../utils/getTokenBalances'

export const ODIN_FEE = 0.000001 // written 0.0000201 BTC on website, but in reality 100 sats
export const MIN_ODIN_SWAP_AMOUNT = 0.000005
const MIN_BTC_WITHDRAW_AMOUNT = 0.0000005 // ~0.05 USD
const MIN_BTC_DEPOSIT_AMOUNT = 0.00003 // ~3 USD

const useBridgeOdin = () => {
  const [inputAmount, setInputAmount] = useState('')
  const [bridgeInProgress, setBridgeInProgress] = useState(false)
  const [success, setSuccess] = useState(false)
  const [bridgeMode, setBridgeMode] = useState<'deposit' | 'withdraw'>('deposit')
  const [selectedToken, setSelectedToken] = useState<PlugToken>()

  const { tokens } = useConfig()
  const { findBalance, refreshBalance } = useBalances()
  const { toAmount, setAmount } = useSwapContext()
  const { wallet } = useWallet()
  const { addToken } = useUserTokens()
  const { fireEvent } = useGoogleAnalytics()
  const { t } = useTranslation()
  const {
    currentQuote: quote,
    performSwap,
    maxAmount: maxSwapAmount,
  } = useSwap({ fromTokenId: ICP_ICP_ID, toTokenId: CKBTC_ICP_ID })

  const [icpToken, ckBTCToken, odinBTCToken] = [ICP_ICP_ID, CKBTC_ICP_ID, ODINBTC_ID].map((id) => ({
    token: tokens.find((t) => t.id === id),
    balance: findBalance({ id })?.balance || 0,
  }))

  useEffect(() => {
    ckBTCToken.token && refreshBalance(ckBTCToken.token)
  }, [ckBTCToken.token?.id])

  useEffect(() => {
    odinBTCToken.token && refreshBalance(odinBTCToken.token)
  }, [odinBTCToken.token?.id])

  useEffect(() => {
    icpToken.token && refreshBalance(icpToken.token)
  }, [icpToken.token?.id])

  const isIcpSelected = bridgeMode === 'deposit' ? selectedToken?.id === ICP_ICP_ID : false
  const amount = Number(inputAmount)
  const btcAmount = isIcpSelected ? Number(toAmount) : amount

  let tokenInfo, fee
  if (bridgeMode === 'deposit') {
    tokenInfo = isIcpSelected ? icpToken : ckBTCToken
    fee = (tokenInfo.token?.fee || 0) * 10 ** -(tokenInfo.token?.decimals || 8)
  } else {
    tokenInfo = odinBTCToken
    fee = 0
  }

  const maxAmount = Math.max(isIcpSelected ? maxSwapAmount : tokenInfo.balance - fee, 0)
  const receiveAmount = Math.max(btcAmount - ODIN_FEE, 0)

  const error = useMemo(() => {
    if (!amount) return t('send.errors.noAmount')

    if (amount > tokenInfo.balance) {
      return t('send.errors.lowBalance', { token: tokenInfo.token?.symbol })
    }

    if (amount + fee > tokenInfo.balance) {
      return t('defi.odin.errors.notEnoughTokenForFee', { token: tokenInfo.token?.symbol })
    }

    const minAmount = bridgeMode === 'deposit' ? MIN_BTC_DEPOSIT_AMOUNT : MIN_BTC_WITHDRAW_AMOUNT
    if (btcAmount < minAmount) {
      return t(`defi.odin.errors.${bridgeMode}TooSmall`, { minAmount: formatNumber(minAmount) })
    }
  }, [amount, tokenInfo])

  const waitOnCKBTCBalance = async (minBalance: number) => {
    const maxRetryCount = 5
    let currentRetry = 0

    if (!ckBTCToken.token) return Promise.reject('ckBTC token not found')

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

    while (currentRetry < maxRetryCount) {
      currentRetry += 1
      const balance = await getTokenBalance(wallet, ckBTCToken.token)
      if (balance >= minBalance) return

      await sleep(100)
    }

    return Promise.reject('Required balance was not met')
  }

  const performBridge = async () => {
    const sourceToken = bridgeMode === 'deposit' ? ckBTCToken.token : odinBTCToken.token
    if (!sourceToken) return Promise.reject('No token config')

    setBridgeInProgress(true)

    try {
      if (bridgeMode === 'deposit' && isIcpSelected) {
        if (!quote) throw new Error('No quote available')

        const received = await performSwap(quote.pair, quote.price)
        fireEvent(GAEventName.SWAP, {
          type: 'swap',
          from_token: icpToken.token?.symbol!,
          to_token: ckBTCToken.token?.symbol!,
          amount: Number(inputAmount),
          amount_received: received / 10 ** ckBTCToken.token?.decimals!,
        })
      }

      await waitOnCKBTCBalance(btcAmount)

      const fromAmount = btcAmount * 10 ** sourceToken.decimals

      const identity = await keychainManager.getIdentity(wallet.principalId)
      const agent = getAgent({ identity })

      if (bridgeMode === 'deposit') {
        await approveToken({
          spender: { address: ODIN_BACKEND_CANISTER },
          tokenCanisterId: CKBTC_LEDGER,
          standard: sourceToken.standard,
          amount: fromAmount.toString(),
        })

        const odinClient = new OdinClient(agent)
        const depositResult = await odinClient.depositCkBTC(fromAmount)

        if ('err' in depositResult) {
          return Promise.reject(JSON.stringify(depositResult.err))
        }

        if ('ok' in depositResult) {
          fireEvent(GAEventName.DEPOSIT_ODIN, { amount })
          setSuccess(true)
        }
      } else {
        const odinClient = new OdinClient(agent)
        const withdrawResponse = await odinClient.withdrawToken(
          'btc',
          wallet.principalId,
          Math.floor(fromAmount).toString()
        )
        if ('err' in withdrawResponse) {
          return Promise.reject(JSON.stringify(withdrawResponse.err))
        }

        fireEvent(GAEventName.WITHDRAW_ODIN, { amount })
        setSuccess(true)
      }
    } finally {
      addToken(wallet.principalId, ckBTCToken.token?.id!)
      addToken(wallet.principalId, odinBTCToken.token?.id!)
      setBridgeInProgress(false)

      setTimeout(() => {
        refreshBalance(ckBTCToken.token!)
        refreshBalance(odinBTCToken.token!)
      }, 3000)
    }
  }

  const onInputAmountChanged = (newInputAmount: string) => {
    setInputAmount(newInputAmount)
    setAmount(newInputAmount)
  }

  return {
    inputAmount,
    setInputAmount: onInputAmountChanged,
    error,
    performBridge,
    bridgeInProgress,
    bridgeMode,
    setBridgeMode,
    token: tokenInfo.token,
    setToken: setSelectedToken,
    maxAmount,
    receiveAmount,
    success,
    slippage: quote?.slippage,
    icpToken,
    ckBTCToken,
  }
}

export default useBridgeOdin
